import resolvePieveApiBaseUrl from '@/lib/resolvePieveApiBaseUrl.js';

const hasValue = (value) => {
  return Boolean(value);
};

const createEmptyPieveRuntimeSurface = () => {
  return {
    workspaces: {
      repo: {
        pickAvailable: false,
        branches: {
          listAvailable: false,
          switchAvailable: false,
          createAndSwitchAvailable: false
        }
      }
    },
    tools: {
      shell: {
        runAvailable: false
      },
      files: {
        readAvailable: false
      },
      search: {
        runAvailable: false
      }
    },
    audit: {
      runBundles: {
        revealAvailable: false,
        pathAvailable: false
      }
    },
    terminal: {
      createAvailable: false,
      attachAvailable: false,
      writeAvailable: false,
      resizeAvailable: false,
      closeAvailable: false
    },
    updates: {
      getStateAvailable: false,
      installAvailable: false
    },
    approvals: {
      respondAvailable: false
    },
    interactions: {
      respondAvailable: false
    },
    sharedWorkspaces: {
      pullOnceAvailable: false,
      sync: {
        startAvailable: false,
        stopAvailable: false
      }
    },
    window: {
      drag: {
        startAvailable: false,
        moveAvailable: false,
        endAvailable: false
      }
    },
    planRuns: {
      startAvailable: false
    }
  };
};

const hasWindowDrag = (client) => {
  const dragFromWindow = (hasValue(client?.window?.drag?.start) || client?.window?.drag?.startAvailable === true) && (hasValue(client?.window?.drag?.move) || client?.window?.drag?.moveAvailable === true) && (hasValue(client?.window?.drag?.end) || client?.window?.drag?.endAvailable === true);
  const dragFromAlias = (hasValue(client?.windowDrag?.start) || client?.windowDrag?.startAvailable === true) && (hasValue(client?.windowDrag?.move) || client?.windowDrag?.moveAvailable === true) && (hasValue(client?.windowDrag?.end) || client?.windowDrag?.endAvailable === true);

  return Boolean(dragFromWindow || dragFromAlias);
};

const getRuntimeKind = (value = {}) => {
  return String(value?.kind || '')
    .trim()
    .toLowerCase();
};

const getRuntimePlatform = (value = {}) => {
  return String(value?.platform || '')
    .trim()
    .toLowerCase();
};

export const getPieveRuntimeSurface = (client = null) => {
  const source = client || {};
  return {
    workspaces: {
      repo: {
        pickAvailable: hasValue(source?.workspaces?.repo?.pick),
        branches: {
          listAvailable: hasValue(source?.workspaces?.repo?.branches?.list),
          switchAvailable: hasValue(source?.workspaces?.repo?.branches?.switch),
          createAndSwitchAvailable: hasValue(source?.workspaces?.repo?.branches?.createAndSwitch)
        }
      }
    },
    tools: {
      shell: {
        runAvailable: hasValue(source?.tools?.shell?.run)
      },
      files: {
        readAvailable: hasValue(source?.tools?.files?.read)
      },
      search: {
        runAvailable: hasValue(source?.tools?.search?.run)
      }
    },
    audit: {
      runBundles: {
        revealAvailable: hasValue(source?.audit?.runBundles?.reveal),
        pathAvailable: hasValue(source?.audit?.runBundles?.path)
      }
    },
    terminal: {
      createAvailable: hasValue(source?.terminal?.create),
      attachAvailable: hasValue(source?.terminal?.attach),
      writeAvailable: hasValue(source?.terminal?.write),
      resizeAvailable: hasValue(source?.terminal?.resize),
      closeAvailable: hasValue(source?.terminal?.close)
    },
    updates: {
      getStateAvailable: hasValue(source?.updates?.getState),
      installAvailable: hasValue(source?.updates?.install)
    },
    approvals: {
      respondAvailable: hasValue(source?.approvals?.respond)
    },
    interactions: {
      respondAvailable: hasValue(source?.interactions?.respond)
    },
    sharedWorkspaces: {
      pullOnceAvailable: hasValue(source?.sharedWorkspaces?.pullOnce),
      sync: {
        startAvailable: hasValue(source?.sharedWorkspaces?.sync?.start),
        stopAvailable: hasValue(source?.sharedWorkspaces?.sync?.stop)
      }
    },
    window: {
      drag: {
        startAvailable: hasValue(source?.window?.drag?.start) || hasValue(source?.windowDrag?.start),
        moveAvailable: hasValue(source?.window?.drag?.move) || hasValue(source?.windowDrag?.move),
        endAvailable: hasValue(source?.window?.drag?.end) || hasValue(source?.windowDrag?.end)
      }
    },
    // Pieve Desktop's plan-run bridge (`window.pieve.planRuns`): Claude Code on this Mac, paid by the person's plan.
    planRuns: {
      startAvailable: hasValue(source?.planRuns?.start)
    }
  };
};

export const getPieveRuntimeMetadata = (client = null) => {
  const source = client || {};

  return {
    kind: getRuntimeKind(source),
    platform: getRuntimePlatform(source),
    surface: getPieveRuntimeSurface(source),
    apiBaseUrl: resolvePieveApiBaseUrl()
  };
};

export const getPieveRuntimeCapabilitiesFromRuntimeMetadata = (runtimeMetadata = null) => {
  const metadata = runtimeMetadata || {};
  const surface = metadata?.surface || createEmptyPieveRuntimeSurface();
  const isDesktop = metadata?.kind === 'desktop' || (metadata?.kind !== 'web' && metadata?.platform && metadata?.platform !== 'web');
  const canListRepoBranches = surface?.workspaces?.repo?.branches?.listAvailable === true;
  const canSwitchRepoBranch = surface?.workspaces?.repo?.branches?.switchAvailable === true;
  const canRespondToInteractions = surface?.interactions?.respondAvailable === true || surface?.approvals?.respondAvailable === true;
  const canRespondToApprovals = surface?.approvals?.respondAvailable === true || surface?.interactions?.respondAvailable === true;
  const canUseTerminal = surface?.terminal?.createAvailable === true && surface?.terminal?.attachAvailable === true && surface?.terminal?.writeAvailable === true && surface?.terminal?.resizeAvailable === true && surface?.terminal?.closeAvailable === true;

  return {
    isDesktop,
    canPickRepo: surface?.workspaces?.repo?.pickAvailable === true,
    canListRepoBranches,
    canSwitchRepoBranch,
    canManageRepoBranches: canListRepoBranches && canSwitchRepoBranch,
    canCreateAndSwitchRepoBranch: surface?.workspaces?.repo?.branches?.createAndSwitchAvailable === true,
    canRunShell: surface?.tools?.shell?.runAvailable === true,
    canReadFile: surface?.tools?.files?.readAvailable === true,
    canRunSearch: surface?.tools?.search?.runAvailable === true,
    canRunAuditBundleReveal: surface?.audit?.runBundles?.revealAvailable === true,
    canCopyRunBundlePath: surface?.audit?.runBundles?.pathAvailable === true,
    canUseTerminal,
    canUseAutoUpdates: surface?.updates?.getStateAvailable === true && surface?.updates?.installAvailable === true,
    canRespondToApprovals,
    canRespondToInteractions,
    canReadSharedWorkspaces: surface?.sharedWorkspaces?.pullOnceAvailable === true,
    canControlSharedSync: surface?.sharedWorkspaces?.sync?.startAvailable === true || surface?.sharedWorkspaces?.sync?.stopAvailable === true,
    canWindowDrag: hasWindowDrag(surface),
    canRunPlanRuns: surface?.planRuns?.startAvailable === true
  };
};

export const getPieveRuntimeCapabilities = (client = null) => {
  return getPieveRuntimeCapabilitiesFromRuntimeMetadata(getPieveRuntimeMetadata(client));
};

export { createEmptyPieveRuntimeSurface };
